import React, { useState, useEffect } from 'react';
import { axiosInstance as axios } from '../../context/AuthContext';
import { FaMoneyBillWave } from 'react-icons/fa';
import { useSubscription, FEATURES } from '../../hooks/useSubscription';
import UpgradeModal from '../UpgradeModal';

// Import refactored components
import {
  DuesOverview,
  DuesMyAccount,
  DuesAllAccounts,
  DuesSettings,
  DuesConfigModal,
  OnlinePaymentModal,
  AddAccountModal,
  ChargeAllModal,
  AllocateToEventModal,
  AdminPaymentModal
} from './dues';

const API = '/api';

export function DuesTab({ familyId, family, members, proxyMembers, currentUser, hasPermission, isOwner }) {
  const { hasFeature } = useSubscription();
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [duesConfig, setDuesConfig] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [summary, setSummary] = useState(null);
  const [myAccount, setMyAccount] = useState(null);
  const [myPayments, setMyPayments] = useState([]);
  const [allPayments, setAllPayments] = useState([]);
  const [allocations, setAllocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeSubTab, setActiveSubTab] = useState('overview');
  const [events, setEvents] = useState([]);
  
  // Modal states
  const [showConfigForm, setShowConfigForm] = useState(false);
  const [showOnlinePaymentForm, setShowOnlinePaymentForm] = useState(false);
  const [showAccountForm, setShowAccountForm] = useState(false);
  const [showChargeForm, setShowChargeForm] = useState(false);
  const [showAllocateForm, setShowAllocateForm] = useState(false);
  const [showAdminPaymentForm, setShowAdminPaymentForm] = useState(false);
  
  // Form states
  const [configForm, setConfigForm] = useState({ billing_cycle: 'monthly', due_day: 1, adult_amount: 0, senior_amount: 0, youth_amount: 0, child_amount: 0 });
  const [onlinePaymentAmount, setOnlinePaymentAmount] = useState('');
  const [onlinePaymentProcessing, setOnlinePaymentProcessing] = useState(false);
  const [accountForm, setAccountForm] = useState({ member_id: '', is_proxy: false, category: 'adult' });
  const [billingPeriod, setBillingPeriod] = useState('');
  const [allocateForm, setAllocateForm] = useState({ event_id: '', amount: '', notes: '' });
  const [selectedMemberForPayment, setSelectedMemberForPayment] = useState(null);
  const [adminPaymentAmount, setAdminPaymentAmount] = useState('');
  const [adminPaymentNotes, setAdminPaymentNotes] = useState('');
  
  // Permissions
  const canManageDues = isOwner || (hasPermission && hasPermission('manage_dues'));
  const canChargeDues = isOwner || (hasPermission && hasPermission('charge_dues'));
  const canAllocateFunds = isOwner || (hasPermission && hasPermission('allocate_funds'));

  useEffect(() => {
    fetchDuesData();
    fetchEvents();
  }, [familyId]);

  const fetchDuesData = async () => {
    try {
      const configRes = await axios.get(`${API}/families/${familyId}/dues-config`, { withCredentials: true });
      const config = configRes.data.config || configRes.data;
      if (config && config.billing_cycle) {
        setDuesConfig(config);
        const amounts = {};
        (config.category_amounts || []).forEach(ca => { amounts[ca.category] = ca.amount; });
        setConfigForm({ billing_cycle: config.billing_cycle, due_day: config.due_day, adult_amount: amounts.adult || 0, senior_amount: amounts.senior || 0, youth_amount: amounts.youth || 0, child_amount: amounts.child || 0 });
      }
      
      try {
        const accountsRes = await axios.get(`${API}/families/${familyId}/dues-accounts`, { withCredentials: true });
        setAccounts(accountsRes.data.accounts || []);
        setSummary(accountsRes.data.summary);
      } catch (e) { }
      
      try {
        const myAccountRes = await axios.get(`${API}/families/${familyId}/dues-accounts/my-account`, { withCredentials: true });
        setMyAccount(myAccountRes.data.account);
        setMyPayments(myAccountRes.data.payments || []);
      } catch (e) { }
      
      try {
        const paymentsRes = await axios.get(`${API}/families/${familyId}/dues-payments`, { withCredentials: true });
        setAllPayments(paymentsRes.data.payments || []);
      } catch (e) { }
      
      try {
        const allocRes = await axios.get(`${API}/families/${familyId}/dues-allocations`, { withCredentials: true });
        setAllocations(allocRes.data.allocations || []);
      } catch (e) { }
    } catch (error) {
      console.error('Error fetching dues config:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchEvents = async () => {
    try {
      const res = await axios.get(`${API}/families/${familyId}/events`, { withCredentials: true });
      setEvents(res.data);
    } catch (error) {
      console.error('Error fetching events:', error);
    }
  };

  const handleSaveConfig = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/families/${familyId}/dues-config`, {
        family_id: familyId, billing_cycle: configForm.billing_cycle, due_day: parseInt(configForm.due_day),
        category_amounts: [
          { category: 'adult', amount: parseFloat(configForm.adult_amount) || 0 },
          { category: 'senior', amount: parseFloat(configForm.senior_amount) || 0 },
          { category: 'youth', amount: parseFloat(configForm.youth_amount) || 0 },
          { category: 'child', amount: parseFloat(configForm.child_amount) || 0 }
        ]
      }, { withCredentials: true });
      await axios.post(`${API}/onboarding/complete-step?step=setup_dues`, {}, { withCredentials: true }).catch(() => {});
      setShowConfigForm(false);
      fetchDuesData();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to save configuration');
    }
  };

  const handleOnlinePayment = async (e) => {
    e.preventDefault();
    setOnlinePaymentProcessing(true);
    try {
      const res = await axios.post(`${API}/payments/checkout`, { payment_type: 'dues', family_id: familyId, amount: parseFloat(onlinePaymentAmount), notes: 'Online dues payment', origin_url: window.location.origin }, { withCredentials: true });
      if (res.data.checkout_url) window.location.href = res.data.checkout_url;
      else throw new Error('No checkout URL received');
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to initiate payment');
      setOnlinePaymentProcessing(false);
    }
  };

  const handleCreateAccount = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/families/${familyId}/dues-accounts`, { member_id: accountForm.member_id, is_proxy: accountForm.is_proxy, category: accountForm.category }, { withCredentials: true });
      setShowAccountForm(false);
      setAccountForm({ member_id: '', is_proxy: false, category: 'adult' });
      alert('Dues account created successfully!');
      fetchDuesData();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to create account');
    }
  };

  const handleChargeAll = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/families/${familyId}/dues-accounts/charge-all?billing_period=${encodeURIComponent(billingPeriod)}`, {}, { withCredentials: true });
      alert('All members charged successfully');
      setShowChargeForm(false);
      setBillingPeriod('');
      fetchDuesData();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to charge members');
    }
  };

  const handleAllocateToEvent = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/families/${familyId}/dues-allocations`, { event_id: allocateForm.event_id, amount: parseFloat(allocateForm.amount), notes: allocateForm.notes }, { withCredentials: true });
      alert('Funds allocated to event');
      setShowAllocateForm(false);
      setAllocateForm({ event_id: '', amount: '', notes: '' });
      fetchDuesData();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to allocate funds');
    }
  };

  const handleAdminRecordPayment = async (e) => {
    e.preventDefault();
    if (!selectedMemberForPayment) return;
    try {
      const res = await axios.post(`${API}/families/${familyId}/dues-payments/on-behalf/${selectedMemberForPayment.member_id}?is_proxy=${selectedMemberForPayment.is_proxy}`, { amount: parseFloat(adminPaymentAmount), notes: adminPaymentNotes }, { withCredentials: true });
      alert(`Payment recorded for ${selectedMemberForPayment.member_name}! Receipt: ${res.data.receipt_number}`);
      setShowAdminPaymentForm(false);
      setSelectedMemberForPayment(null);
      setAdminPaymentAmount('');
      setAdminPaymentNotes('');
      fetchDuesData();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to record payment');
    }
  };

  // Get available members for dues accounts
  const allMembers = [...members.map(m => ({ id: m.user_id, name: m.name, is_proxy: false })), ...proxyMembers.map(p => ({ id: p.proxy_id, name: p.name, is_proxy: true }))];
  const accountedMemberIds = new Set(accounts.map(a => a.member_id));
  const availableMembers = allMembers.filter(m => !accountedMemberIds.has(m.id));

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600 mx-auto"></div>
        <p className="mt-4 text-gray-600">Loading dues information...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="dues-tab">
      <UpgradeModal isOpen={showUpgradeModal} onClose={() => setShowUpgradeModal(false)} feature="dues_management" requiredTier="growing" />
      
      {!hasFeature(FEATURES.DUES_MANAGEMENT) ? (
        <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl border border-blue-200 p-8 text-center">
          <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <FaMoneyBillWave className="w-8 h-8 text-blue-600" />
          </div>
          <h3 className="text-xl font-bold text-gray-900 mb-2">Dues Management</h3>
          <p className="text-gray-600 mb-6 max-w-md mx-auto">Track family contributions, manage payments, and allocate funds to events. Available on the Growing plan and above.</p>
          <button onClick={() => setShowUpgradeModal(true)} className="px-6 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors">
            Upgrade to Growing
          </button>
        </div>
      ) : (
        <>
          {/* Sub-tabs */}
          <div className="flex space-x-4 border-b">
            <button onClick={() => setActiveSubTab('overview')} className={`px-4 py-2 font-medium ${activeSubTab === 'overview' ? 'border-b-2 border-emerald-600 text-emerald-600' : 'text-gray-600'}`}>Overview</button>
            <button onClick={() => setActiveSubTab('my-account')} className={`px-4 py-2 font-medium ${activeSubTab === 'my-account' ? 'border-b-2 border-emerald-600 text-emerald-600' : 'text-gray-600'}`}>My Account</button>
            {canManageDues && (
              <>
                <button onClick={() => setActiveSubTab('all-accounts')} className={`px-4 py-2 font-medium ${activeSubTab === 'all-accounts' ? 'border-b-2 border-emerald-600 text-emerald-600' : 'text-gray-600'}`}>All Members</button>
                <button onClick={() => setActiveSubTab('settings')} className={`px-4 py-2 font-medium ${activeSubTab === 'settings' ? 'border-b-2 border-emerald-600 text-emerald-600' : 'text-gray-600'}`}>Settings</button>
              </>
            )}
          </div>

          {activeSubTab === 'overview' && (
            <DuesOverview duesConfig={duesConfig} summary={summary} allocations={allocations} canManageDues={canManageDues} canChargeDues={canChargeDues} canAllocateFunds={canAllocateFunds} accounts={accounts} onEditConfig={() => setShowConfigForm(true)} onAddAccount={() => setShowAccountForm(true)} onChargeAll={() => setShowChargeForm(true)} onAllocate={() => setShowAllocateForm(true)} />
          )}
          {activeSubTab === 'my-account' && (
            <DuesMyAccount myAccount={myAccount} myPayments={myPayments} onPayOnline={() => setShowOnlinePaymentForm(true)} />
          )}
          {activeSubTab === 'all-accounts' && canManageDues && (
            <DuesAllAccounts accounts={accounts} allPayments={allPayments} onAddAccount={() => setShowAccountForm(true)} onRecordPayment={(account) => { setSelectedMemberForPayment(account); setShowAdminPaymentForm(true); }} />
          )}
          {activeSubTab === 'settings' && canManageDues && (
            <DuesSettings duesConfig={duesConfig} onEditConfig={() => setShowConfigForm(true)} />
          )}

          {/* Modals */}
          <DuesConfigModal show={showConfigForm} configForm={configForm} setConfigForm={setConfigForm} onSave={handleSaveConfig} onClose={() => setShowConfigForm(false)} />
          <OnlinePaymentModal show={showOnlinePaymentForm} myAccount={myAccount} amount={onlinePaymentAmount} setAmount={setOnlinePaymentAmount} processing={onlinePaymentProcessing} onSubmit={handleOnlinePayment} onClose={() => { setShowOnlinePaymentForm(false); setOnlinePaymentAmount(''); }} />
          <AddAccountModal show={showAccountForm} accountForm={accountForm} setAccountForm={setAccountForm} availableMembers={availableMembers} onSubmit={handleCreateAccount} onClose={() => setShowAccountForm(false)} />
          <ChargeAllModal show={showChargeForm} billingPeriod={billingPeriod} setBillingPeriod={setBillingPeriod} onSubmit={handleChargeAll} onClose={() => setShowChargeForm(false)} />
          <AllocateToEventModal show={showAllocateForm} summary={summary} events={events} allocateForm={allocateForm} setAllocateForm={setAllocateForm} onSubmit={handleAllocateToEvent} onClose={() => setShowAllocateForm(false)} />
          <AdminPaymentModal show={showAdminPaymentForm} selectedMember={selectedMemberForPayment} amount={adminPaymentAmount} setAmount={setAdminPaymentAmount} notes={adminPaymentNotes} setNotes={setAdminPaymentNotes} onSubmit={handleAdminRecordPayment} onClose={() => { setShowAdminPaymentForm(false); setSelectedMemberForPayment(null); setAdminPaymentAmount(''); setAdminPaymentNotes(''); }} />
        </>
      )}
    </div>
  );
}
